/**
 * The administration screens' calls that are not on the shared `Api`
 * interface. Each is one line over `api.send`, so adding a screen here never
 * touches api.ts.
 */
import type { SpecialActionRow } from '@project/contracts'
import type { Api, ChangelogEntry, PermissionRow } from './api.js'

const q = (params: Readonly<Record<string, string | undefined>>): string => {
  const usable = Object.entries(params).filter((e): e is [string, string] => e[1] !== undefined && e[1] !== '')
  return usable.length === 0 ? '' : `?${new URLSearchParams(usable).toString()}`
}

/* ---- approval routes (WF-006 … WF-013) ---- */

export type ScenarioAction = 'ApproveAndContinue' | 'ApproveAndFinish'
export interface ScenarioCondition { field: 'total' | 'projectId' | 'partyId' | 'categoryId'; op: string; value: string | string[] }
export interface ScenarioView {
  when: 'ALWAYS' | 'CONDITIONAL'
  conditions?: ScenarioCondition[]
  action: ScenarioAction
  approvers: readonly string[] | 'ANYONE_WITH_PERMISSION'
}
export interface RouteStageView {
  order: number
  name: string
  action: string
  approvers: readonly string[] | 'ANYONE_WITH_PERMISSION'
  scenarios?: ScenarioView[]
}
export interface RouteAlert { action: string; projectRoleIds: string[]; roleIds: string[]; userIds: string[] }
export interface RouteView {
  id: string
  wfNo: string | null
  description: string | null
  companyId: string
  appliesTo: string[]
  companies: string[]
  overrideUserIds: string[]
  overrideUsers: string[]
  alerts: RouteAlert[]
  createdAt: string | null
  createdByName: string | null
  docType: string
  docTypeLabel: string
  name: string
  version: number
  status: 'LIVE' | 'DRAFT' | 'ARCHIVED'
  isLive: boolean
  stages: RouteStageView[]
  inFlight: number
}
export interface RouteInput {
  docType: string
  name: string
  description: string | null
  stages: Array<{ name: string; action: ScenarioAction; scenarios: ScenarioView[] }>
  asDraft: boolean
  appliesTo: string[]
  overrideUserIds: string[]
  alerts: RouteAlert[]
}

/* ---- people (USR-001 … USR-011) ---- */

export interface UserRecord {
  id: string
  name: string
  email: string
  role: string
  active: boolean
  lastLoginAt: string | null
  mustChangePassword: boolean
  lockedUntil: string | null
  userType: string
  projectScope: string
  communicationEmail: string | null
  landline: string | null
  createdAt: string | null
  employee: { id: string; name: string; designation: string | null; roleName: string | null; roleId: string | null; mobile: string | null } | null
}

export interface NewUserForm {
  name: string
  email: string
  designation: string | null
  userType: 'ADMIN' | 'NON_ADMIN' | 'DELEGATE'
  roleId: string | null
  companyIds: string[]
  communicationEmail: string | null
  mobile: string | null
  landline: string | null
  projectScope: 'ALL' | 'ASSIGNED'
  projectIds: string[]
  toolsScope: string
  stockPurchaseScope: string
  reportDownloadScope: string
  expenseReportScope: string
  delegateDays?: number
}

export interface RegisterRow {
  id: string
  email: string
  at: string
  ip: string | null
  browser: string | null
  os: string | null
  outcome: string
  userId: string | null
  userName: string | null
  roleName: string | null
}

export interface DelegateRequest {
  id: string
  requestedName: string
  requestedEmail: string
  reason: string | null
  days: number
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVOKED'
  requestedByName: string | null
  requestedAt: string
  decidedByName: string | null
  decidedAt: string | null
  userId: string | null
}

/* ---- setup records (SET-020 … SET-025) ---- */

export interface CompanyRecord {
  id: string
  name: string
  legalName: string
  abbreviation: string
  gstin: string | null
  panNo: string | null
  email: string | null
  active: boolean
  tanNo: string | null
  cinNo: string | null
  landline: string | null
  website: string | null
  isTraining: boolean
  hasLogo: boolean
}
export interface CompanyAddress {
  id: string; label: string | null; line1: string | null; line2: string | null; city: string | null
  state: string | null; stateCode: string | null; postalCode: string | null; country: string; gstin: string | null; isDefault: boolean
}
export interface CompanyTaxUnit { id: string; name: string; gstin: string; stateCode: string; addressId: string | null; addressLabel: string | null; active: boolean }
export interface CompanySetup { addresses: CompanyAddress[]; taxUnits: CompanyTaxUnit[] }

export interface NumberRule {
  id: string
  docType: string
  docTypeLabel: string
  prefix: string
  nextValue: number
  width: number
  financialYear: string
  active: boolean
  format: string
  scope: string
  resetPeriod: string
  jobTypePlaceholder: string
  example: string
}

export interface PermissionChange { readonly role: string; readonly docType: string; readonly level: string }

/**
 * Downloads the rows a list is showing as an Excel workbook. Binary, so it is
 * fetched directly rather than through the JSON request helper.
 */
const downloadRows = async (
  api: Api, title: string, headers: readonly string[], rows: ReadonlyArray<ReadonlyArray<string | number | null>>,
): Promise<void> => {
  const res = await fetch('/api/admin/export', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', 'x-company': api.companyId },
    body: JSON.stringify({ title, headers, rows }),
  })
  if (!res.ok) throw new Error(`The export failed (${res.status}).`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${title.replace(/[^A-Za-z0-9 _-]+/g, '').trim().replace(/\s+/g, '-') || 'export'}.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const adminApi = (api: Api) => ({
  exportRows: (title: string, headers: readonly string[], rows: ReadonlyArray<ReadonlyArray<string | number | null>>) =>
    downloadRows(api, title, headers, rows),
  /* ---- permissions ---- */
  savePermissions: (changes: readonly PermissionChange[]) =>
    api.send<readonly PermissionRow[]>('/admin/permissions/batch', { changes }),
  specialActions: () => api.send<readonly SpecialActionRow[]>('/admin/special-actions'),
  saveSpecialAction: (row: { docType: string; action: string; mode: 'ALL' | 'SPECIFIC'; roles: readonly string[] }) =>
    api.send<readonly SpecialActionRow[]>('/admin/special-actions', row),
  saveSpecialActions: (changes: ReadonlyArray<{ docType: string; action: string; mode: 'ALL' | 'SPECIFIC'; roles: readonly string[] }>) =>
    api.send<readonly SpecialActionRow[]>('/admin/special-actions/batch', { changes }),
  changelog: (filter: { entity?: string; docType?: string; role?: string; limit?: number }) =>
    api.send<readonly ChangelogEntry[]>(`/admin/changelog${q({
      entity: filter.entity, docType: filter.docType, role: filter.role,
      limit: filter.limit === undefined ? undefined : String(filter.limit),
    })}`),
  users: () => api.send<UserRecord[]>('/admin/users'),
  user: (id: string) => api.send<UserRecord>(`/admin/users/${id}`),
  createUser: (form: NewUserForm) => api.send<{ user: UserRecord; temporaryPassword: string }>('/admin/users/full', form),
  updateProfile: (id: string, input: { name?: string; email?: string; designation?: string | null; roleId?: string | null; mobile?: string | null; communicationEmail?: string | null; landline?: string | null }) =>
    api.send<UserRecord>(`/admin/users/${id}/profile`, input),
  register: (filter: { show?: string; from?: string; to?: string; userId?: string }) =>
    api.send<RegisterRow[]>(`/admin/login-register${q(filter)}`),
  delegateRequests: () => api.send<DelegateRequest[]>('/admin/delegate-requests'),
  requestDelegate: (input: { name: string; email: string; reason: string | null; days: number }) =>
    api.send<{ id: string }>('/admin/delegate-requests', input),
  decideDelegate: (id: string, decision: 'APPROVE' | 'REJECT' | 'REVOKE') =>
    api.send<{ requests: DelegateRequest[]; temporaryPassword: string | null }>(`/admin/delegate-requests/${id}/decide`, { decision }),
  companies: () => api.send<CompanyRecord[]>('/admin/companies'),
  saveCompany: (c: Partial<CompanyRecord> & { name: string; legalName: string; abbreviation: string; active: boolean; logo?: string | null }) =>
    api.send<CompanyRecord[]>('/admin/companies', c),
  companySetup: (id: string) => api.send<CompanySetup>(`/admin/companies/${id}/setup`),
  saveAddress: (companyId: string, a: Partial<CompanyAddress> & { label: string }) =>
    api.send<CompanySetup>(`/admin/companies/${companyId}/addresses`, a),
  defaultAddress: (companyId: string, addressId: string) =>
    api.send<CompanySetup>(`/admin/companies/${companyId}/addresses/${addressId}/default`, {}),
  removeAddress: (companyId: string, addressId: string) =>
    api.send<CompanySetup>(`/admin/companies/${companyId}/addresses/${addressId}/remove`, {}),
  removeTaxUnit: (companyId: string, unitId: string) =>
    api.send<CompanySetup>(`/admin/companies/${companyId}/tax-units/${unitId}/remove`, {}),
  saveTaxUnit: (companyId: string, u: { id?: string; name: string; gstin: string; addressId: string | null; active?: boolean }) =>
    api.send<CompanySetup>(`/admin/companies/${companyId}/tax-units`, u),
  numbering: () => api.send<NumberRule[]>('/admin/numbering'),
  saveNumbering: (rule: NumberRule) => api.send<NumberRule[]>('/admin/numbering', rule),
  tokens: (docType: string) => api.send<ReadonlyArray<{ token: string; means: string }>>(`/admin/numbering/tokens${q({ docType })}`),
  routes: () => api.send<RouteView[]>('/admin/workflows'),
  routeTypes: () => api.send<ReadonlyArray<{ key: string; label: string }>>('/admin/workflows/types'),
  saveRoute: (input: RouteInput) => api.send<RouteView[]>('/admin/workflows', input),
  setRouteStatus: (id: string, status: 'LIVE' | 'DRAFT' | 'ARCHIVED') =>
    api.send<RouteView[]>(`/admin/workflows/${id}/status`, { status }),
  copyRoute: (id: string, into: { docType?: string; companyId?: string } = {}) =>
    api.send<RouteView[]>(`/admin/workflows/${id}/copy`, into),
  setRouteReach: (id: string, reach: { overrideUserIds?: string[]; appliesTo?: string[]; description?: string | null }) =>
    api.send<RouteView[]>(`/admin/workflows/${id}/reach`, reach),
  /** Which special actions of a document type the signed-in person may run. */
  mySpecialActions: (docType: string) =>
    api.send<{ allowed: readonly string[]; denied: readonly string[] }>(`/access/special-actions/${encodeURIComponent(docType)}`),
})

export type AdminApi = ReturnType<typeof adminApi>
